import { scry, scryImageUrl } from "../lib/scryClient";
// web/src/data/prices.ts
import {
  collectionGroup,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  doc,
  getDoc,
  type DocumentData,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { PriceSnapshot } from "@/types/cards";

// simple in-memory cache
const mem = new Map<string, { vendors?: PriceSnapshot; sg?: number | null }>();

/**
 * Normalize whatever shape we have in Firestore into a consistent vendor snapshot:
 *  - supports { vendors: { tcgplayer, cardkingdom, starcitygames, scryfall } }
 *  - supports top-level { tcgplayer, cardkingdom, starcitygames, scryfall }
 *  - supports flat keys (tcg_low, tcg_market, ck_nmint, scg_low, etc.)
 */
function normalizeVendors(raw: any): PriceSnapshot | null {
  if (!raw || typeof raw !== "object") return null;

  const v = raw.vendors || raw;

  // Build vendors from either nested objects or flat fields
  const tcg = {
    low: num(v.tcgplayer?.low ?? v.tcg_low),
    market: num(v.tcgplayer?.market ?? v.tcg_market),
    mid: num(v.tcgplayer?.mid ?? v.tcg_mid),
    high: num(v.tcgplayer?.high ?? v.tcg_high),
  };

  // If no TCG mid yet, map Scryfall USD to TCG mid (Scryfall uses TCG mid)
  if (!tcg.mid) {
    const sfUsd = num(v.scryfall?.usd ?? v.usd);
    if (sfUsd) tcg.mid = sfUsd;
  }
  // Optionally map foil to a higher TCG bucket if nothing set
  if (!tcg.high) {
    const sfFoil = num(v.scryfall?.usd_foil ?? v.usd_foil);
    if (sfFoil) tcg.high = sfFoil;
  }

  const ck = {
    retail: num(v.cardkingdom?.retail ?? v.ck_retail ?? v.ck_nm ?? v.ck_nmint),
    buylist: num(v.cardkingdom?.buylist ?? v.ck_buylist),
  };

  // Map SCG to a single retail-like value; prefer mid, fallback to low/market/high
  const scgMid =
    num(v.starcity?.retail) ??
    num(v.scg_mid) ??
    num(v.scg_low) ??
    num(v.scg_market) ??
    num(v.scg_high);
  const sc = { retail: scgMid };

  const hasT = hasAny(tcg);
  const hasK = hasAny(ck);
  const hasS = hasAny(sc);

  if (!hasT && !hasK && !hasS) return null;

  const out: PriceSnapshot = {};
  if (hasT) out.tcgplayer = tcg;
  if (hasK) out.cardkingdom = ck;
  if (hasS) out.starcity = sc;
  return out;
}

function hasAny(obj: Record<string, any>): boolean {
  return Object.values(obj).some(
    (v) => typeof v === "number" && Number.isFinite(v),
  );
}
function num(n: unknown): number | undefined {
  const x = typeof n === "string" ? Number(n) : (n as number | undefined);
  return typeof x === "number" && Number.isFinite(x) ? x : undefined;
}

export async function readVendorPrices(
  scryfallId: string,
): Promise<PriceSnapshot | null> {
  if (mem.get(scryfallId)?.vendors) return mem.get(scryfallId)!.vendors!;

  let normalized: PriceSnapshot | null = null;
  try {
    const ref = doc(db, "prices", scryfallId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      normalized = normalizeVendors(snap.data());
    }
  } catch {
    // ignore and fall back
  }

  // Last resort: use Scryfall to populate TCGplayer.mid (+ foil -> high)
  if (!normalized) {
    try {
      const res = await scry.getById(scryfallId);
      if (res.ok) {
        const j = await res.json();
        const mid = num(j?.prices?.usd);
        const high = num(j?.prices?.usd_foil);
        if (mid || high) normalized = { tcgplayer: { mid, high } } as PriceSnapshot;
      }
    } catch {
      // ignore
    }
  }

  // Always return stable buckets so downstream optional chains are safe
  if (!normalized) {
    normalized = { tcgplayer: {}, cardkingdom: {}, starcity: {} } as PriceSnapshot;
  }

  mem.set(scryfallId, { ...(mem.get(scryfallId) || {}), vendors: normalized });
  return normalized;
}

/** Spellgather floor = min price among *public* user sale listings for this Scryfall id.
 *  Supports both legacy `status: "sale"` AND new `for_sale: true`.
 */
export async function readSpellgatherFloor(
  scryfallId: string,
): Promise<number | null> {
  const cached = mem.get(scryfallId)?.sg;
  if (cached !== undefined) return cached ?? null;

  // Query 1: legacy schema
  const qLegacy = query(
    collectionGroup(db, "cards"),
    where("scryfall_id", "==", scryfallId),
    where("status", "==", "sale"),
    orderBy("price", "asc"),
    limit(10),
  );

  // Query 2: new flag schema
  const qFlag = query(
    collectionGroup(db, "cards"),
    where("scryfall_id", "==", scryfallId),
    where("for_sale", "==", true),
    orderBy("price", "asc"),
    limit(10),
  );

  let prices: number[] = [];
  try {
    const [snapLegacy, snapFlag] = await Promise.all([getDocs(qLegacy), getDocs(qFlag)]);

    const pull = (d: DocumentData) => {
      const p = d?.price;
      return typeof p === "number" && Number.isFinite(p) ? p : null;
    };

    const allDocs = [...snapLegacy.docs, ...snapFlag.docs];

    // Dedupe by full path (same doc could satisfy both queries)
    const byPath = new Map<string, number>();
    for (const docSnap of allDocs) {
      const p = pull(docSnap.data());
      if (p != null && !byPath.has(docSnap.ref.path)) byPath.set(docSnap.ref.path, p);
    }
    prices = Array.from(byPath.values());
  } catch {
    // ignore — fall back to null below
  }

  const val = prices.length ? Math.min(...prices) : null;
  mem.set(scryfallId, { ...(mem.get(scryfallId) || {}), sg: val });
  return val;
}

export function formatUsd(n?: number | null): string {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  return `$${n.toFixed(2)}`;
}


